import type { ReactNode } from "react";

interface TooltipProps {
  /** Tooltip text. Falsy values render the children alone (no tooltip). */
  label?: string | null;
  children: ReactNode;
  /** Where the bubble sits relative to the trigger. Default top. */
  side?: "top" | "bottom";
  /**
   * Wrap longer copy across multiple lines instead of the default
   * single-line bubble. Use for explanatory tooltips (a sentence or
   * two), not status hints.
   */
  multiline?: boolean;
  className?: string;
}

/**
 * Hover/focus tooltip. Wraps children in a span that exposes the label
 * on hover and on keyboard focus (so disabled buttons inside still
 * surface the message — :hover doesn't fire on disabled buttons in
 * every browser, but the wrapper span receives the event regardless).
 *
 * Pass `label={null}` (or empty string) to render the children alone —
 * useful when the same trigger is conditionally annotated.
 */
export function Tooltip({
  label,
  children,
  side = "top",
  multiline = false,
  className = "",
}: TooltipProps) {
  if (!label) return <>{children}</>;

  const layout = multiline
    ? "w-max max-w-[260px] whitespace-normal text-left font-normal leading-relaxed"
    : "whitespace-nowrap font-medium";

  const sidePos =
    side === "top"
      ? "bottom-full mb-1.5"
      : "top-full mt-1.5";
  const arrowPos =
    side === "top"
      ? "top-full -translate-y-px border-x-transparent border-b-transparent border-t-ink"
      : "bottom-full translate-y-px border-x-transparent border-t-transparent border-b-ink";

  return (
    <span className={`group relative inline-flex ${className}`}>
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 ${sidePos} shadow-card invisible rounded bg-ink px-2 py-1 text-[11px] text-paper-hi opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 ${layout}`}
      >
        {label}
        <span
          aria-hidden
          className={`absolute left-1/2 h-0 w-0 -translate-x-1/2 border-[4px] ${arrowPos}`}
        />
      </span>
    </span>
  );
}
